import React, { ChangeEvent, useEffect, useState } from "react";
import styled from "@emotion/styled";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DateFormat, MergeWithOverwrite, i18n } from "src/common";
import { colors } from "src/theme";
import { InputLabel } from "src/components";

dayjs.extend(customParseFormat);

const Input = styled.input((props: { hasError: boolean }) => ({
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  width: '100%',
  height: '44px',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontFamily: "Red Hat Display",
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  backgroundColor: 'transparent',
  borderRadius: '8px',
  border: `1px solid ${props.hasError ? colors.error1 : colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',

  '&[disabled]': {
    border: `1px solid ${colors.border6}`,
    color: colors.text4,
    pointerEvents: 'none' as const,
  },

  '&::-moz-placeholder': {
    color: colors.text4,
    opacity: 1,
  },

  '&:-ms-input-placeholder': {
    color: colors.text4,
  },

  '&::-webkit-input-placeholder': {
    color: colors.text4,
  },

  '&:focus::-moz-placeholder': {
    color: 'transparent',
    opacity: 1,
  },

  '&:focus:-ms-input-placeholder': {
    color: 'transparent',
  },

  '&:focus::-webkit-input-placeholder': {
    color: 'transparent',
  },

  '&:focus': {
    borderColor: colors.border4,
  },

  '&[type=number]::-webkit-inner-spin-button, &[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
  },
}));

const ValidationError = styled.div({
  position: 'absolute',
  color: colors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
});

const findAddedCharacter = (originalString: string, newString: string) => {
  const minLength = Math.min(originalString.length, newString.length);

  // Pronađi prvu poziciju na kojoj se stringovi razlikuju
  let diffIndex = -1;
  for (let i = 0; i < minLength; i++) {
    if (originalString[i] !== newString[i]) {
      // Provjeri je li znak na trenutnoj poziciji u newString '/'
      // i je li znak na prethodnoj poziciji u newString također '/'
      if (newString[i] === '/' && newString[i - 1] === '/') {
        // Ako jest, pretpostavimo da je dodan '/' na pogrešno mjesto
        diffIndex = i - 1;
      } else {
        diffIndex = i;
      }
      break;
    }
  }

  // Ako nema razlike u prvih minLength znakova, dodani znak je na kraju novog stringa
  if (diffIndex === -1) {
    return {
      position: newString.length - 1,
      character: newString[newString.length - 1]
    };
  }
  // Ako je pronađena razlika, dodani znak je na poziciji diffIndex u novom stringu
  return {
    position: diffIndex,
    character: newString[diffIndex]
  };

}

export type InputBirthDateProps = MergeWithOverwrite<React.InputHTMLAttributes<HTMLInputElement>, {
  label?: string;
  value?: string;
  error?: string;
  onChange?: (value: string) => void;
  // Birth dates can't be in the future; reuse this input for other dates (e.g. policy
  // cancellation) by allowing future dates and skipping the max-age check.
  allowFuture?: boolean;
}
>;

// format input-a DD/MM/YYYY
// format output-a / value YYYY-MM-DD

export const InputBirthDate: React.FC<InputBirthDateProps> = ({
  label,
  value = "",
  error = '',
  onChange,
  allowFuture = false,
  ...props
}) => {
  const formatToInputValue = (originalValue: string): string => {
    const originalValueFormatted = originalValue ? dayjs(originalValue).format(DateFormat) : '';
    const [year, month, day] = originalValueFormatted.split('-');
    const formattedDate = `${day ? `${day}/` : ''}${month ? `${month}/` : ''}${year ?? ''}`;
    return formattedDate;
  };

  const [inputValue, setInputValue] = useState(formatToInputValue(value));

  // Controlled from outside (e.g. prefilled after a fetch): adopt a complete date or a reset,
  // but never a partial value, so typing is not interrupted.
  useEffect(() => {
    if (value !== '' && value.length !== 10) return;
    const next = formatToInputValue(value);
    if (next !== inputValue) setInputValue(next);
  }, [value]);
  const [validationError, setValidationError] = useState('');

  const formatToOutputValue = (originalValue: string): string => {
    const [day, month, year] = originalValue.split('/');
    const formattedDate = `${year ? `${year}-` : ''}${month ? `${month}-` : ''}${day ?? ''}`;
    return formattedDate;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value.replace(/[^\d/]/g, ""); // Remove all non-numeric characters except '/'

    // Ensure input length does not exceed 10 characters (DD/MM/YYYY)
    if (input.length > 10) {
      input = input.slice(0, 10);
    }

    // Add slashes at the correct positions
    if (input.length > inputValue.length) {
      const addedChar = findAddedCharacter(inputValue, input);
      if (addedChar.character === '/' && (addedChar.position !== 2 && addedChar.position !== 5)) {
        return; // Do nothing if '/' is at a wrong position
      }
      const [day] = input.split('/');
      if (input.length > 2 && day?.length && day.length >= 2 && input[2] !== '/') {
        input = `${input.slice(0, 2)}/${input.slice(2)}`;
      }
      const [day2, month2] = input.split('/');
      if (input.length > 5 && month2?.length && month2.length >= 2 && day2?.length && day2.length >= 2 && input[5] !== '/') {
        input = `${input.slice(0, 5)}/${input.slice(5)}`;
      }
    } else {
      // Remove slashes when the input length decreases
      if (input.length === 3 && input[2] === '/') {
        input = input.slice(0, 2);
      }
      if (input.length === 6 && input[5] === '/') {
        input = input.slice(0, 5);
      }
    }

    setInputValue(input);

    if (input.length !== 10) {
      setValidationError('');
      if (onChange) onChange(formatToOutputValue(input));
      return;
    }

    const parsed = dayjs(input, 'DD/MM/YYYY', true);
    const now = dayjs();
    const maxAge = 120;
    const minBirthDate = now.subtract(maxAge, 'year');

    if (!parsed.isValid()) {
      setValidationError(i18n.t('validation.birthDate.invalid'));
      if (onChange) onChange('');
    } else if (!allowFuture && parsed.isAfter(now)) {
      setValidationError(i18n.t('validation.birthDate.past'));
      if (onChange) onChange('');
    } else if (!allowFuture && parsed.isBefore(minBirthDate)) {
      setValidationError(i18n.t('validation.birthDate.maxAge', { maxAge }));
      if (onChange) onChange('');
    } else {
      setValidationError('');
      if (onChange) onChange(parsed.format(DateFormat));
    }
  }

  return (
    <div>
      {label ? <InputLabel htmlFor={props.id} disabled={props.disabled}>{label}</InputLabel> : null}
      <Input
        autoComplete="off"
        {...props}
        type='tel'
        id={props.id}
        value={inputValue}
        onChange={handleChange}
        hasError={!!(validationError || error)}
        placeholder="DD/MM/GGGG"
        maxLength={10}
      />
      {(validationError || error) ? <ValidationError>{validationError || error}</ValidationError> : null}
    </div>
  );
};
